import { useIntl } from '@/intl'
import { HighlightedPaymentMethod } from '@/scopes/prices/components/HighlightedPaymentMethod'
import { BookNowButton } from '@/scopes/prices/components/PriceBasket/BookNowButton'
import { ShareButton } from '@/scopes/prices/components/PriceBasket/ShareButton'
import { transportResponseSelector } from '@/scopes/prices/components/selectors'
import { usePricesSelector } from '@/scopes/prices/infrastructure/usePricesSelector'
import {
  Collapsible,
  CollapsibleRoot,
  CollapsibleTrigger,
  mqMin,
  space,
  tok,
} from '@sunwebgroup/ui-components'
import { Banner, Icon, IconGlyph, Typography } from '@sunwebgroup/ui-components/uds'
import * as React from 'react'
import { useMemo } from 'react'
import { css, styled } from 'styled-components'

const PACKAGE_USP: Record<string, IconGlyph> = {
  Discount: 'OptionalFacilities',
  FreeCancellation: 'ThumbsUp',
  AccoCampaigns: 'CheckMark',
  CarRentalIncluded: 'CarFront',
  DownPayment: 'Deposit',
  HandLuggageIncluded: 'LuggageCheck',
  TransferIncluded: 'BusRight',
}

const Section = styled(CollapsibleRoot)`
  border-bottom: none;
  padding: 0;
  &:first-of-type {
    border-top: 2px solid ${tok('semantics_dividers_color')};
    margin-top: ${space('primitives_space_16')};
    @media ${mqMin('primitives_breakpoint_m_min')} {
      margin-top: 0;
    }
  }
  &:nth-of-type(2) {
    background-color: ${tok('semantics_views_tinted_background_color')};
  }
`

const CollapsibleHeader = styled.div`
  display: flex;
  justify-content: space-between;
  padding: ${space('primitives_space_24')} ${space('primitives_space_8')};
  cursor: pointer;

  @media ${mqMin('primitives_breakpoint_l_min')} {
    padding: ${space('primitives_space_24')} ${space('primitives_space_16')};
  }

  ${Typography} {
    ${Typography.modVariant('body-m-bold-tight')}
  }
`

const Content = styled.div`
  display: flex;
  flex-direction: column;
  gap: ${space('primitives_space_12')};
  padding: ${space('primitives_space_16')};
  padding-top: 0;
  @media ${mqMin('primitives_breakpoint_l_min')} {
    padding: ${space('primitives_space_32')};
    padding-top: 0;
  }

`

const Trailing = styled.div`
  display: flex;
  ${Icon}:last-of-type {
    transition: transform 200ms ease-out;
    margin-left: auto !important;
  }

  &[data-state='open'] {
    ${Icon}:last-of-type {
      transform: rotate(180deg);
    }
  }
`

const TextLine = styled.div`
  display: flex;
  justify-content: space-between;
`

const GrossPrice = styled.div`
  display: flex;
  justify-content: flex-end;
  color: ${tok('semantics_content_secondary_color')};
  text-decoration: line-through;
`

const SubtotalsWrapper = styled.div`
  padding: ${space('primitives_space_16')} 0 ;
`

const Divider = styled.div`
  width: 100%;
  border-top: 2px solid ${tok('semantics_dividers_color')};
  margin-top: ${space('primitives_space_16')};
  margin-bottom: ${space('primitives_space_16')};
`

const BookButton = styled(BookNowButton)`
  width: 100%;
`
const FooterWrapper = styled.div`
  display: flex;
  flex-direction: column;
  gap: ${space('primitives_space_16')}
`

const ExtraCostsWrapper = styled.div`
  margin-top: ${space('primitives_space_32')}
`

const UspsWrapper = styled.div`
  display: flex;
  flex-direction: column;
  justify-content: space-between;

  @media ${mqMin('primitives_breakpoint_m_min')} {
    flex-direction: row;
  }
`

export function PriceBreakdown() {
  const intl = useIntl()
  const { priceBreakdown, paymentMethod, transportType } = usePricesSelector(transportResponseSelector)
  const [reservationCostsOpen, setReservationCostsOpen] = React.useState(true)
  const [destinationCostsOpen, setDestinationCostsOpen] = React.useState(true)

  if (!priceBreakdown)
    return null

  const hasDiscount = priceBreakdown.totalGrossPrice > priceBreakdown.totalPrice

  return (
    <>
      <Section defaultOpen onOpenChange={setReservationCostsOpen} open={reservationCostsOpen}>
        <CollapsibleTrigger asChild>
          <CollapsibleHeader>
            <Typography>{intl.formatMessage({ id: 'price_basket_price_breakdown_pay_reservation' })}</Typography>
            <Trailing>
              {priceBreakdown.atReservation.subtotal > 0 && (
                <Typography>{intl.formatCurrency(priceBreakdown.atReservation.subtotal)}</Typography>
              )}
              <Icon glyph={reservationCostsOpen ? 'ChevronUpExtraSmall' : 'ChevronDownExtraSmall'} />
            </Trailing>
          </CollapsibleHeader>
        </CollapsibleTrigger>
        <Collapsible>
          <Content>
            {priceBreakdown.atReservation.rooms.map((item, index) => (
              <RoomItem key={index} transportType={transportType} id={index + 1} items={item} />
            ))}
            {priceBreakdown.atReservation.services.map((item, index) => (
              <PriceBreakdownItem key={index} {...item} variant={item.price >= 0 ? 'default' : 'discount'} />
            ))}
            {priceBreakdown.atReservation.subtotal > 0 && (
              <PriceBreakdownItem
                header={intl.formatMessage({ id: 'price_basket_price_breakdown_subtotal' })}
                price={priceBreakdown.atReservation.subtotal}
                variant="bold"
              />
            )}
          </Content>
        </Collapsible>
      </Section>
      {priceBreakdown.atDestination.subtotal > 0 && (
        <Section defaultOpen onOpenChange={setDestinationCostsOpen} open={destinationCostsOpen}>
          <CollapsibleTrigger asChild>
            <CollapsibleHeader>
              <Typography>{intl.formatMessage({ id: 'price_basket_price_breakdown_pay_destination' })}</Typography>
              <Trailing>
                <Typography>{intl.formatCurrency(priceBreakdown.atDestination.subtotal)}</Typography>
                <Icon glyph={destinationCostsOpen ? 'ChevronUpExtraSmall' : 'ChevronDownExtraSmall'} />
              </Trailing>
            </CollapsibleHeader>
          </CollapsibleTrigger>
          <Collapsible>
            <Content>
              {priceBreakdown.atDestination.items.map((item, index) => (
                <PriceBreakdownItem key={index} {...item} variant={item.price ? 'default' : 'discount'} />
              ))}
              <PriceBreakdownItem
                header={intl.formatMessage({ id: 'price_basket_price_breakdown_subtotal' })}
                price={priceBreakdown.atDestination.subtotal}
                variant="bold"
              />
            </Content>
            {priceBreakdown.extraCostsText && (
              <ExtraCostsWrapper>
                <Banner text={priceBreakdown.extraCostsText.title} supportingText={priceBreakdown.extraCostsText.text} size="M" color="neutral" />
              </ExtraCostsWrapper>
            )}
          </Collapsible>
        </Section>
      )}
      <SubtotalsWrapper>
        {hasDiscount && (
          <GrossPrice>
            <Typography $variant="body-s-regular-tight">{intl.formatCurrency(priceBreakdown.totalGrossPrice)}</Typography>
          </GrossPrice>
        )}
        <TextLine>
          <Typography $variant="body-l-bold-tight">
            {intl.formatMessage({ id: 'price_basket_price_breakdown_total' })}
          </Typography>
          <Typography $variant="body-l-bold-tight">{intl.formatCurrency(priceBreakdown.totalPrice)}</Typography>
        </TextLine>
        <TextLine>
          <Typography $variant="body-s-regular-tight">{intl.formatMessage({ id: 'price_basket_price_breakdown_price_per_person' })}</Typography>
          <Typography $variant="body-s-regular-tight">{intl.formatCurrency(priceBreakdown.pricePerPerson)}</Typography>
        </TextLine>
        <Divider />
        {priceBreakdown.atReservation.subtotal > 0 && (
          <TextLine>
            <Typography $variant="body-s-regular-tight">{intl.formatMessage({ id: 'price_basket_price_breakdown_pay_reservation' })}</Typography>
            <Typography $variant="body-s-regular-tight">{intl.formatCurrency(priceBreakdown.atReservation.subtotal)}</Typography>
          </TextLine>
        )}
        {priceBreakdown.atDestination.subtotal > 0 && (
          <TextLine>
            <Typography $variant="body-s-regular-tight">{intl.formatMessage({ id: 'price_basket_price_breakdown_pay_destination' })}</Typography>
            <Typography $variant="body-s-regular-tight">{intl.formatCurrency(priceBreakdown.atDestination.subtotal)}</Typography>
          </TextLine>
        )}
      </SubtotalsWrapper>
      <FooterWrapper>
        <BookButton />
        <ShareButton />
        {paymentMethod?.text && paymentMethod?.icon && (<HighlightedPaymentMethod text={paymentMethod?.text} icon={paymentMethod?.icon} />)}
        <Banner color="neutral" icon="CarDoor" text={intl.formatMessage({ id: 'price_basket_price_breakdown_extra_costs_message' })} size="M" />
        <UspsWrapper>
          {priceBreakdown.usps.map((usp, index) => (
            <Usp key={index} {...usp} />
          ))}
        </UspsWrapper>
      </FooterWrapper>
    </>
  )
}

const StrongText = styled(Typography)`
  color: ${tok('semantics_content_secondary_color')};
`

const Wrapper = styled.div<{ $variant?: 'bold' | 'discount' | 'default' }>`
  ${({ $variant }) =>
    $variant === 'bold'
    && css`
      ${Typography} {
        ${Typography.modVariant('body-m-bold-tight')}
      }
    `}
  ${({ $variant }) =>
    $variant === 'discount'
    && css`
      ${StrongText}, ${Typography} {
        color: ${tok('primitives_color_success_500')}
      }
    `}
`

const Header = styled.div`
  display: flex;
  justify-content: space-between;
`

const AgeProfileWrapper = styled.div`
  display: flex;
  justify-content: space-between;
`

interface RoomItemProps {
  id: number
  transportType?: string
  items: {
    price: number
    valueText: string
    description: string
    isDiscount: boolean
  }[]
}
function RoomItem({ id, items, transportType }: RoomItemProps) {
  const intl = useIntl()
  const header = intl.formatMessage({ id: `prices_room_selector_room_group-title` }, { roomNumber: id })
  const getTransportTypeText = useMemo(() => {
    if (transportType === 'Flight') {
      return ` + ${intl.formatMessage({ id: 'prices_room_selector_room_group-transport-flight' })}`
    }
    if (transportType === 'Bus') {
      return ` + ${intl.formatMessage({ id: 'prices_room_selector_room_group-transport-bus' })}`
    }
    return ''
  }, [intl, transportType])
  return (
    <Wrapper>
      <Header>
        <Typography>
          {header}
          {getTransportTypeText}
        </Typography>
        <StrongText>{intl.formatCurrency(items.reduce((acc, item) => acc + item.price, 0))}</StrongText>
      </Header>
      {items.map((item, index) => (
        <AgeProfileWrapper key={index}>
          <StrongText>{item.valueText}</StrongText>
        </AgeProfileWrapper>
      ))}
    </Wrapper>
  )
}
interface PriceBreakdownItemProps {
  header: string
  price: number
  valueText?: string
  description?: string
  variant?: 'bold' | 'discount' | 'default'
}

function PriceBreakdownItem({ header, price, valueText, description, variant }: PriceBreakdownItemProps) {
  const intl = useIntl()
  const trailing = price ? `${intl.formatCurrency(price, variant === 'discount')}` : valueText

  return (
    <Wrapper $variant={variant}>
      <Header>
        <Typography>{header}</Typography>
        <StrongText>{trailing}</StrongText>
      </Header>
      {description && <StrongText>{description}</StrongText>}
    </Wrapper>
  )
}

const StyledUsp = styled.div`
  display: flex;
  flex-direction: row;
  align-items: center;
  margin-bottom: ${space('primitives_space_16')};

  @media ${mqMin('primitives_breakpoint_m_min')} {
    flex-direction: column;
    flex: 1;
    text-align: center;
  }

  ${Icon} {
    margin-right: ${space('primitives_space_4')};

    @media ${mqMin('primitives_breakpoint_m_min')} {
      margin-right: 0;
    }
  }

  ${Typography} {
    &:nth-of-type(3) {
      color: ${tok('semantics_content_secondary_color')};
    }
  }
`

const TextWrapper = styled.div`
  display: flex;
  flex-direction: column;
`

function Usp({ type, title, text }: { type: string, title: string, text?: string | undefined }) {
  return (
    <StyledUsp>
      <Icon glyph={PACKAGE_USP[type]} />
      <TextWrapper>
        <Typography $variant="body-m-bold-tight">{title}</Typography>
        {text && <Typography $variant="body-m-regular-tight">{text}</Typography>}
      </TextWrapper>
    </StyledUsp>
  )
}
